import React, { useState } from 'react';
import './ResetPasswordPopup.css';

interface ResetPasswordPopupProps {
  isOpen: boolean;
  username: string;
  email: string;
  onClose: () => void;
  onResetPassword: (username: string, pin: string, password: string) => Promise<void>;
  onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
  onSuccessGoToLogin?: () => void;
}

const ResetPasswordPopup: React.FC<ResetPasswordPopupProps> = ({
  isOpen,
  username,
  email,
  onClose,
  onResetPassword,
  onShowToast,
  onSuccessGoToLogin,
}) => {
  const [pin, setPin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [locked, setLocked] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pin.trim()) {
      setError('Vui lòng nhập mã PIN');
      return;
    }

    if (!newPassword.trim()) {
      setError('Vui lòng nhập mật khẩu mới');
      return;
    }

    if (newPassword.length < 6) {
      setError('Mật khẩu phải ít nhất 6 ký tự');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Mật khẩu xác nhận không khớp');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onResetPassword(username, pin, newPassword);
      onShowToast('Đặt lại mật khẩu thành công!', 'success');
      setPin('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
      // Delay một chút để user có thể thấy toast trước khi chuyển popup
      setTimeout(() => {
        if (onSuccessGoToLogin) {
          onSuccessGoToLogin();
        }
      }, 500);
    } catch (err) {
      console.error('[ResetPasswordPopup] Error resetting password:', err);
      const msg = (err as any)?.message || 'Có lỗi xảy ra khi đặt lại mật khẩu';
      setError(msg);
      const code = (err as any)?.code ?? (err as any)?.response?.code ?? null;
      // If backend indicates too many wrong attempts, lock reset button until resend
      if (code === -1001 || code === -1005) {
        setLocked(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPin('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  // Resend PIN handler with 60s cooldown
  const handleResendPin = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setError('');
    try {
      // call forgotPassword via authService through parent toast handler
      // parent Map provides authService access; we call backend via service import here to avoid changing parent
      const auth = await import('../../services/auth.service');
      const result = await auth.default.forgotPassword(username);
      if (result && result.success) {
        onShowToast(result.message || 'Mã mới đã được gửi', 'success');
        // reset locked state when a new PIN is successfully generated
        setLocked(false);
        // update email displayed if backend returns email
        // start cooldown 60s
        setResendCooldown(60);
        const iv = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(iv);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        // if backend returns specific code, reflect it in UI
        if (result && typeof (result as any).code !== 'undefined' && (result as any).code === -1001) {
          setLocked(true);
        }
        setError(result.message || 'Không thể gửi mã mới');
      }
    } catch (err: any) {
      console.error('[ResetPasswordPopup] resend pin error', err);
      setError(err?.message || 'Có lỗi khi gửi mã mới');
    } finally {
      setResendLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="reset-password-overlay" onClick={handleClose}>
      <div className="reset-password-popup" onClick={(e) => e.stopPropagation()}>
        <div className="reset-password-header">
          <h2 className="reset-password-title">Đặt lại mật khẩu</h2>
          <button className="reset-password-close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label className="email-info">Mã PIN đã được gửi đến: <strong>{email}</strong></label>
          </div>

          <div className="form-group">
            <label htmlFor="reset-pin">Mã PIN</label>
            <input
              type="text"
              id="reset-pin"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Nhập mã PIN từ email"
              required
              disabled={loading}
              maxLength={6}
            />
          </div>

          <div className="form-group">
            <label htmlFor="reset-new-password">Mật khẩu mới</label>
            <input
              type="password"
              id="reset-new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Nhập mật khẩu mới (ít nhất 6 ký tự)"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="reset-confirm-password">Xác nhận mật khẩu</label>
            <input
              type="password"
              id="reset-confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Xác nhận mật khẩu mới"
              required
              disabled={loading}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="submit" className="reset-password-submit-btn" disabled={loading || locked}>
              {loading ? 'Đang xử lý...' : locked ? 'Tạm khóa - yêu cầu mã mới' : 'Đặt lại mật khẩu'}
            </button>
            <button
              type="button"
              className="reset-password-resend-btn"
              onClick={handleResendPin}
              disabled={resendCooldown > 0 || resendLoading}
            >
              {resendLoading ? 'Đang gửi...' : resendCooldown > 0 ? `Gửi lại (${resendCooldown}s)` : 'Gửi mã mới'}
            </button>
          </div>
        </form>

        {/* Show error message in the footer area instead of static info */}
        {error ? (
          <div className="error-message" style={{ margin: '12px 20px' }}>{error}</div>
        ) : (
          <p className="reset-password-info">
            Vui lòng nhập mã PIN từ email và tạo mật khẩu mới.
          </p>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPopup;
